import { useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Languages,
  Search,
  Edit2,
  Save,
  X,
  Plus,
  RefreshCw,
  Download,
  CheckCircle2,
  AlertCircle,
  Globe,
  Loader2,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";
import { trpc } from "@/utils/trpc";

const CATEGORIES = [
  { id: "all", label: "All" },
  { id: "common", label: "Common" },
  { id: "auth", label: "Authentication" },
  { id: "nav", label: "Navigation" },
  { id: "dashboard", label: "Dashboard" },
  { id: "requests", label: "Requests" },
  { id: "approvals", label: "Approvals" },
  { id: "sites", label: "Sites & Facilities" },
  { id: "users", label: "Users" },
  { id: "settings", label: "Settings" },
  { id: "validation", label: "Validation" },
  { id: "actions", label: "Actions" },
  { id: "general", label: "General" },
];

const PAGE_SIZE = 100;

export default function TranslationManagement() {
  const { i18n } = useTranslation();
  const isRTL = i18n.dir() === "rtl";

  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("all");
  const [status, setStatus] = useState<"all" | "done" | "missing" | "modified">("all");
  const [page, setPage] = useState(1);

  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editValues, setEditValues] = useState({ en: "", ar: "" });

  const [addOpen, setAddOpen] = useState(false);
  const [newEntry, setNewEntry] = useState({ key: "", category: "common", en: "", ar: "" });

  const [deleteKey, setDeleteKey] = useState<string | null>(null);

  const utils = trpc.useUtils();

  const { data: statsData, isLoading: statsLoading, refetch: refetchStats } = trpc.translations.stats.useQuery(
    undefined,
    { refetchOnWindowFocus: false, staleTime: 0 }
  );

  const { data: listData, isLoading: listLoading, refetch: refetchList } = trpc.translations.list.useQuery(
    {
      search: search || undefined,
      category: category !== "all" ? category : undefined,
      status: status !== "all" ? status : undefined,
      page,
      pageSize: PAGE_SIZE,
    },
    { refetchOnWindowFocus: false, staleTime: 0 }
  );

  const [isRefreshing, setIsRefreshing] = useState(false);
  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true);
    await Promise.all([refetchStats(), refetchList()]);
    setIsRefreshing(false);
    toast.success("Translations refreshed");
  }, [refetchStats, refetchList]);

  const updateMutation = trpc.translations.update.useMutation({
    onSuccess: () => {
      toast.success("Translation saved");
      setEditingKey(null);
      utils.translations.list.invalidate();
      utils.translations.stats.invalidate();
    },
    onError: (e: any) => toast.error("Save failed", { description: e.message }),
  });

  const addMutation = trpc.translations.add.useMutation({
    onSuccess: () => {
      toast.success("Translation key added");
      setAddOpen(false);
      setNewEntry({ key: "", category: "common", en: "", ar: "" });
      utils.translations.list.invalidate();
      utils.translations.stats.invalidate();
    },
    onError: (e: any) => toast.error("Add failed", { description: e.message }),
  });

  const deleteMutation = trpc.translations.delete.useMutation({
    onSuccess: () => {
      toast.success("Translation key deleted");
      setDeleteKey(null);
      utils.translations.list.invalidate();
      utils.translations.stats.invalidate();
    },
    onError: (e: any) => toast.error("Delete failed", { description: e.message }),
  });

  const startEdit = useCallback((row: any) => {
    setEditingKey(row.key);
    setEditValues({ en: row.en, ar: row.ar });
  }, []);

  const cancelEdit = useCallback(() => {
    setEditingKey(null);
    setEditValues({ en: "", ar: "" });
  }, []);

  const saveEdit = useCallback(() => {
    if (!editingKey) return;
    updateMutation.mutate({ key: editingKey, en: editValues.en, ar: editValues.ar });
  }, [editingKey, editValues, updateMutation]);

  const exportJSON = useCallback((lang: "en" | "ar") => {
    const rows = listData?.rows ?? [];
    const out: any = {};
    rows.forEach((r) => {
      const parts = r.key.split(".");
      let cur = out;
      for (let i = 0; i < parts.length - 1; i++) {
        if (!cur[parts[i]]) cur[parts[i]] = {};
        cur = cur[parts[i]];
      }
      cur[parts[parts.length - 1]] = lang === "en" ? r.en : r.ar;
    });
    const blob = new Blob([JSON.stringify(out, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `translation_${lang}.json`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`${lang.toUpperCase()} translations exported`);
  }, [listData]);

  const stats = statsData ?? { total: 0, translated: 0, missing: 0, modified: 0 };
  const rows = listData?.rows ?? [];
  const totalRows = listData?.total ?? 0;
  const totalPages = Math.ceil(totalRows / PAGE_SIZE);
  const completionPct = stats.total > 0 ? Math.round((stats.translated / stats.total) * 100) : 0;

  const getStatusBadge = (row: any) => {
    if (row.isModified) return <Badge className="bg-purple-100 text-purple-700 border-0">Modified</Badge>;
    if (!row.ar || row.ar === row.en) return <Badge className="bg-amber-100 text-amber-700 border-0">Missing</Badge>;
    return <Badge className="bg-green-100 text-green-700 border-0">Done</Badge>;
  };

  return (
    <div className="space-y-6" dir={isRTL ? "rtl" : "ltr"}>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-[#5B2C93]/10 flex items-center justify-center">
            <Languages className="h-5 w-5 text-[#5B2C93]" />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-[#2C2C2C]">Translation Management</h1>
            <p className="text-sm text-[#6B6B6B]">Manage and edit all translation strings for the application</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={() => exportJSON("en")} className="gap-1.5">
            <Download className="h-4 w-4" /> Export EN
          </Button>
          <Button variant="outline" size="sm" onClick={() => exportJSON("ar")} className="gap-1.5">
            <Download className="h-4 w-4" /> Export AR
          </Button>
          <Button variant="outline" size="sm" onClick={handleRefresh} disabled={isRefreshing} className="gap-1.5">
            <RefreshCw className={`h-4 w-4 ${isRefreshing ? "animate-spin" : ""}`} /> Refresh
          </Button>
          <Button size="sm" onClick={() => setAddOpen(true)} className="bg-[#5B2C93] hover:bg-[#3D1C5E] gap-1.5">
            <Plus className="h-4 w-4" /> Add Translation
          </Button>
        </div>
      </div>

      {/* Completion bar */}
      <Card className="border-[#E0E0E0]">
        <CardContent className="pt-5 pb-4">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Globe className="h-4 w-4 text-[#5B2C93]" />
              <span className="text-sm font-medium text-[#2C2C2C]">Translation Completeness</span>
            </div>
            <Badge className={`${completionPct === 100 ? "bg-green-600" : "bg-[#5B2C93]"} text-white border-0`}>
              {statsLoading ? "..." : `${completionPct}%`}
            </Badge>
          </div>
          <p className="text-xs text-[#6B6B6B] mb-3">
            {statsLoading ? "Loading..." : `${stats.translated} of ${stats.total} strings translated to Arabic`}
          </p>
          <div className="w-full bg-[#F0F0F0] rounded-full h-2">
            <div className="bg-green-500 h-2 rounded-full transition-all duration-500" style={{ width: `${completionPct}%` }} />
          </div>
          <div className="flex justify-between mt-2 text-xs text-[#6B6B6B]">
            <span className="flex items-center gap-1"><CheckCircle2 className="h-3 w-3 text-green-500" /> Translated: {stats.translated}</span>
            <span className="flex items-center gap-1"><AlertCircle className="h-3 w-3 text-amber-500" /> Missing: {stats.missing}</span>
          </div>
        </CardContent>
      </Card>

      {/* Stats cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: "Total Strings", value: stats.total, color: "text-[#2C2C2C]" },
          { label: "Translated", value: stats.translated, color: "text-green-600" },
          { label: "Missing", value: stats.missing, color: "text-amber-600" },
          { label: "Modified", value: stats.modified, color: "text-[#5B2C93]" },
        ].map((s) => (
          <Card key={s.label} className="border-[#E0E0E0]">
            <CardContent className="pt-5 pb-4">
              <p className="text-xs text-[#6B6B6B] mb-1">{s.label}</p>
              <p className={`text-2xl font-semibold ${s.color}`}>
                {statsLoading ? <Loader2 className="h-5 w-5 animate-spin inline" /> : s.value}
              </p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#6B6B6B]" />
          <Input placeholder="Search translations..." value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} className="pl-9 border-[#E0E0E0]" />
        </div>
        <Select value={category} onValueChange={(v) => { setCategory(v); setPage(1); }}>
          <SelectTrigger className="w-[160px] border-[#E0E0E0]"><SelectValue placeholder="Category" /></SelectTrigger>
          <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={status} onValueChange={(v: any) => { setStatus(v); setPage(1); }}>
          <SelectTrigger className="w-[140px] border-[#E0E0E0]"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="done">Done</SelectItem>
            <SelectItem value="missing">Missing</SelectItem>
            <SelectItem value="modified">Modified</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="outline" size="icon" onClick={handleRefresh} disabled={isRefreshing} title="Refresh">
          <RefreshCw className={`h-4 w-4 ${isRefreshing ? "animate-spin" : ""}`} />
        </Button>
      </div>

      {/* Table */}
      <Card className="border-[#E0E0E0]">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-[#FAFAFA]">
                <TableHead className="text-xs font-semibold text-[#6B6B6B] w-[220px]">KEY</TableHead>
                <TableHead className="text-xs font-semibold text-[#6B6B6B] w-[100px]">CATEGORY</TableHead>
                <TableHead className="text-xs font-semibold text-[#6B6B6B]">ENGLISH</TableHead>
                <TableHead className="text-xs font-semibold text-[#6B6B6B]">ARABIC</TableHead>
                <TableHead className="text-xs font-semibold text-[#6B6B6B] w-[100px]">STATUS</TableHead>
                <TableHead className="text-xs font-semibold text-[#6B6B6B] w-[80px] text-right">ACTIONS</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {listLoading ? (
                <TableRow><TableCell colSpan={6} className="text-center py-12"><Loader2 className="h-6 w-6 animate-spin mx-auto text-[#5B2C93]" /></TableCell></TableRow>
              ) : rows.length === 0 ? (
                <TableRow><TableCell colSpan={6} className="text-center py-12 text-[#6B6B6B]">No translations found</TableCell></TableRow>
              ) : (
                rows.map((row) => (
                  <TableRow key={row.key} className="hover:bg-[#FAFAFA]">
                    <TableCell className="font-mono text-xs text-[#5B2C93] py-2">{row.key}</TableCell>
                    <TableCell className="py-2"><Badge variant="outline" className="text-xs capitalize">{row.category}</Badge></TableCell>
                    {editingKey === row.key ? (
                      <>
                        <TableCell className="py-2"><Input value={editValues.en} onChange={(e) => setEditValues((v) => ({ ...v, en: e.target.value }))} className="h-8 text-sm border-[#5B2C93]/40" /></TableCell>
                        <TableCell className="py-2"><Input value={editValues.ar} onChange={(e) => setEditValues((v) => ({ ...v, ar: e.target.value }))} className="h-8 text-sm border-[#5B2C93]/40 text-right" dir="rtl" /></TableCell>
                        <TableCell className="py-2" />
                        <TableCell className="py-2 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button size="icon" variant="ghost" className="h-7 w-7 text-green-600 hover:text-green-700" onClick={saveEdit} disabled={updateMutation.isPending}>
                              {updateMutation.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
                            </Button>
                            <Button size="icon" variant="ghost" className="h-7 w-7 text-[#6B6B6B]" onClick={cancelEdit}><X className="h-3.5 w-3.5" /></Button>
                          </div>
                        </TableCell>
                      </>
                    ) : (
                      <>
                        <TableCell className="py-2 text-sm text-[#2C2C2C] max-w-[200px] truncate">{row.en}</TableCell>
                        <TableCell className="py-2 text-sm text-[#2C2C2C] max-w-[200px] truncate" dir="rtl">
                          {row.ar || <span className="text-[#AAAAAA] italic">Not translated</span>}
                        </TableCell>
                        <TableCell className="py-2">{getStatusBadge(row)}</TableCell>
                        <TableCell className="py-2 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button size="icon" variant="ghost" className="h-7 w-7 text-[#6B6B6B] hover:text-[#5B2C93]" onClick={() => startEdit(row)}><Edit2 className="h-3.5 w-3.5" /></Button>
                            <Button size="icon" variant="ghost" className="h-7 w-7 text-[#6B6B6B] hover:text-red-600" onClick={() => setDeleteKey(row.key)}><Trash2 className="h-3.5 w-3.5" /></Button>
                          </div>
                        </TableCell>
                      </>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-[#E0E0E0]">
            <p className="text-xs text-[#6B6B6B]">Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, totalRows)} of {totalRows}</p>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>Previous</Button>
              <span className="text-xs text-[#6B6B6B]">Page {page} / {totalPages}</span>
              <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages}>Next</Button>
            </div>
          </div>
        )}
      </Card>

      {/* Add Translation Dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Plus className="h-5 w-5 text-[#5B2C93]" /> Add Translation Key</DialogTitle>
            <DialogDescription>Add a new translation string to the system.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label>Key (dot-notation)</Label>
              <Input placeholder="e.g. common.newFeature" value={newEntry.key} onChange={(e) => setNewEntry((v) => ({ ...v, key: e.target.value }))} />
            </div>
            <div className="space-y-1.5">
              <Label>Category</Label>
              <Select value={newEntry.category} onValueChange={(v) => setNewEntry((e) => ({ ...e, category: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.filter((c) => c.id !== "all").map((c) => <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>English</Label>
              <Input placeholder="English text" value={newEntry.en} onChange={(e) => setNewEntry((v) => ({ ...v, en: e.target.value }))} />
            </div>
            <div className="space-y-1.5">
              <Label>Arabic (optional)</Label>
              <Input placeholder="النص العربي" dir="rtl" value={newEntry.ar} onChange={(e) => setNewEntry((v) => ({ ...v, ar: e.target.value }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)}>Cancel</Button>
            <Button className="bg-[#5B2C93] hover:bg-[#3D1C5E]" onClick={() => addMutation.mutate(newEntry)} disabled={!newEntry.key || !newEntry.en || addMutation.isPending}>
              {addMutation.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}Add Key
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirm Dialog */}
      <Dialog open={!!deleteKey} onOpenChange={(o) => !o && setDeleteKey(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600"><Trash2 className="h-5 w-5" /> Delete Translation Key</DialogTitle>
            <DialogDescription>
              Permanently delete <code className="font-mono text-xs bg-gray-100 px-1 py-0.5 rounded">{deleteKey}</code>? This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteKey(null)}>Cancel</Button>
            <Button variant="destructive" onClick={() => deleteKey && deleteMutation.mutate({ key: deleteKey })} disabled={deleteMutation.isPending}>
              {deleteMutation.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
